import React, { useContext, useEffect, useState, useCallback } from 'react';
import { EventContext } from '../context/EventContext';
import { AuthContext } from '../context/AuthContext';
import { Link } from "react-router-dom";
import { toast } from 'react-toastify';
import API_BASE_URL from '../config';
import { EventCardSkeleton } from './Skeleton';

const Event = ({ event }) => {
    const [creator, setCreator] = useState(null);
    const { getAllEvents, attendEvent, unattendEvent, attendingEvents, getEventsAttending, updateEvent, deleteEvent } = useContext(EventContext);
    const [isAttending, setIsAttending] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAttendees, setShowAttendees] = useState(false);
    const [attendLoading, setAttendLoading] = useState(false);

    const { isLoggedIn, userId } = useContext(AuthContext);
    
    const toggleAttendees = () => {
        setShowAttendees(!showAttendees);
    };
    
    const toggleMenu = () => {
        setShowMenu(!showMenu);
    };

    const handleEdit = () => {
        setShowMenu(false);
        setShowEditModal(true);
    };

    const handleDelete = async () => {
        setShowMenu(false);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            await deleteEvent(event._id);
            setShowDeleteModal(false);
            toast.success("Event deleted successfully!");
        } catch (error) {
            // Error toast shown by EventContext
        }
    };

    const handleEditSubmit = async (updatedEvent) => {
        try {
            await updateEvent(event._id, updatedEvent);
            getAllEvents();
            setShowEditModal(false);
            toast.success("Event updated successfully!");
        } catch (error) {
            // Error toast shown by EventContext
        }
    };

    const fetchCreator = useCallback(async (creatorID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/getuser/${creatorID}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json"
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch creator data');
            }

            const resp = await response.json();
            setCreator(resp);
        } catch (error) {
            console.error("Failed to fetch creator data", error);
        }
    }, []);

    useEffect(() => {
        if (localStorage.getItem('token'))
            getEventsAttending();
    }, []);

    useEffect(() => {
        if (event && event._id) {
            fetchCreator(event.createdBy);
        }
    }, [event, fetchCreator]);

    useEffect(() => {
        if (event && event._id && attendingEvents.length > 0) {
            setIsAttending(attendingEvents.some(e => e._id === event._id));
        }
    }, [event, attendingEvents]);

    const handleAttend = async () => {
        if (isLoggedIn) {
            setAttendLoading(true);
            try {
                await attendEvent(event._id);
                getAllEvents();
                getEventsAttending();
                toast.success("You're attending this event!");
            } catch (error) {
                // Error toast handled in EventContext
            } finally {
                setAttendLoading(false);
            }
        } else {
            toast.error("You must be logged in to attend an event.");
        }
    };

    const handleUnattend = async () => {
        if (isLoggedIn) {
            setAttendLoading(true);
            try {
                await unattendEvent(event._id);
                getAllEvents();
                getEventsAttending();
                toast.success("You're no longer attending.");
            } catch (error) {
                // Error toast handled in EventContext
            } finally {
                setAttendLoading(false);
            }
        } else {
            toast.error("You must be logged in to unattend an event.");
        }
    };

    if (!event || !creator) return <EventCardSkeleton />;

    const isHost = userId && String(userId) === String(event.createdBy);

    return (
        <div className="event-container gradient-background">
            <div className="event-header">
                <Link className="event-uploader-container" to={`/profile/${creator._id}`}>
                    <img src={creator.displayPicture} alt="Display Picture" className="event-display-picture" />
                    <div className="event-uploader">{creator.name}</div>
                </Link>
                {isHost && (
                    <div className="dropdown">
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="24" 
                            height="24" 
                            fill="currentColor" 
                            className="bi bi-three-dots-vertical" 
                            viewBox="0 0 16 16"
                            onClick={toggleMenu}
                        >
                            <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/>
                        </svg>
                        {showMenu && (
                            <div className="dropdown-menu show">
                                <button className="dropdown-item" onClick={handleEdit}>
                                    <img className="dropdown-icon" src="/images/edit.png" alt="edit-event" /> Edit
                                </button>
                                <button className="dropdown-item" onClick={handleDelete}>
                                    <img className="dropdown-icon" src="/images/delete.png" alt="delete-event" /> Remove
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="event-details">
                <Link to={`/event/${event._id}`} className="event-title-link">
                    <div className="event-title">{event.title}</div>
                </Link>
                <div className="event-info">
                    {new Date(event.startdatetime).toLocaleString()} - {new Date(event.enddatetime).toLocaleString()}<br />
                    {event.location}
                </div>
                <div className="event-description">
                    {event.description}
                </div>
            </div>
            <div className="event-footer">
                <button type="button" className="btn btn-link showAttendees" onClick={toggleAttendees}>
                    {showAttendees ? 'Hide Attendees' : 'Show Attendees'}
                </button>
                <button
                    type="button"
                    className={isAttending ? "btn-unattend" : "btn-attend"}
                    onClick={isAttending ? handleUnattend : handleAttend}
                    disabled={attendLoading}
                >
                    {attendLoading ? '...' : (isAttending ? 'Unattend' : 'Attend')}
                </button>
            </div>
            {showAttendees && (
                <AttendeesInline
                    attendeeIds={event.attendees}
                    host={API_BASE_URL}
                />
            )}
            {showEditModal && (
                <EditEventInline
                    event={event}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={handleEditSubmit}
                />
            )}
            {showDeleteModal && (
                <DeleteConfirmationInline
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={confirmDelete}
                />
            )}
        </div>
    );
};

const EditEventInline = ({ event, onClose, onSubmit }) => {
    const [editedEvent, setEditedEvent] = useState(event);

    const handleChange = (e) => {
        setEditedEvent({
            ...editedEvent,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (new Date(editedEvent.enddatetime) <= new Date(editedEvent.startdatetime)) {
            toast.error("End date and time must be after start date and time");
            return;
        }
        onSubmit(editedEvent);
    };

    return (
        <div className="event-inline-panel">
            <div className="event-inline-header">
                <h5 className="event-inline-title">Edit Event</h5>
                <button type="button" className="close" onClick={onClose} aria-label="Close">
                    <span>&times;</span>
                </button>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Title</label>
                    <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={editedEvent.title}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label>Description</label>
                    <textarea
                        className="form-control"
                        name="description"
                        value={editedEvent.description}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label>Start Date and Time</label>
                    <input
                        type="datetime-local"
                        className="form-control"
                        name="startdatetime"
                        value={new Date(editedEvent.startdatetime).toISOString().slice(0, 16)}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label>End Date and Time</label>
                    <input
                        type="datetime-local"
                        className="form-control"
                        name="enddatetime"
                        value={new Date(editedEvent.enddatetime).toISOString().slice(0, 16)}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-group">
                    <label>Location</label>
                    <input
                        type="text"
                        className="form-control"
                        name="location"
                        value={editedEvent.location}
                        onChange={handleChange}
                    />
                </div>
                <button type="submit" className="btn btn-primary mt-3">Save changes</button>
                <button type="button" className="btn btn-secondary mt-2 ms-2" onClick={onClose}>Cancel</button>
            </form>
        </div>
    );
};

const DeleteConfirmationInline = ({ onClose, onConfirm }) => (
    <div className="event-inline-panel">
        <div className="event-inline-header">
            <h5 className="event-inline-title">Confirm Deletion</h5>
            <button type="button" className="close" onClick={onClose} aria-label="Close">
                <span>&times;</span>
            </button>
        </div>
        <p style={{ marginBottom: 12 }}>Are you sure you want to delete this event?</p>
        <div>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-danger ms-2" onClick={onConfirm}>Delete</button>
        </div>
    </div>
);

const AttendeesInline = ({ attendeeIds, host }) => {
    const [attendees, setAttendees] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAttendees = async () => {
            try {
                const attendeePromises = attendeeIds.map(id =>
                    fetch(`${host}/api/auth/getuser/${id}`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json"
                        },
                    }).then(res => res.json())
                );
                const attendeeData = await Promise.all(attendeePromises);
                setAttendees(attendeeData);
            } catch (error) {
                console.error("Failed to fetch attendee data", error);
                toast.error("Failed to load attendees. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchAttendees();
    }, [attendeeIds, host]);

    return (
        <div className="event-inline-panel attendees-inline">
            <h5 className="event-inline-title">Attendees</h5>
            {loading ? (
                <div>Loading...</div>
            ) : attendees.length === 0 ? (
                <div>No attendees yet</div>
            ) : (
                <div className="attendees-list">
                    {attendees.map(attendee => (
                        <div key={attendee._id} className="attendee-item mb-3">
                            <Link to={`/profile/${attendee._id}`}>
                                <img src={attendee.displayPicture} alt={attendee.name} className="attendee-avatar" />
                                <span className="attendee-name">{attendee.name}</span>
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Event;
